import { ApolloConsumer } from '@apollo/client';
import { HirerNotificationPreferenceProvider } from '@seek/hirer-notification-preference';
import { HirerTeamMemberJobAccessProvider } from '@seek/hirer-team-member-job-access';
import { useHubble } from '@seek/hubble';
import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { log } from 'src/app/utils/logging';
import { actionResetDraftId as resetSessionDraftId } from 'src/app/v1/state/modules/session/draftId';
import { NotificationsSetting } from 'src/app/v1/views/components/NotificationsSetting';
import type { AppState } from 'src/types/appState';

import { SuccessBodyContent } from '../SuccessBodyContent';

interface Props {
  jobId?: string;
  inNotificationSettings?: boolean;
}
export const SuccessBody = ({
  jobId,
  inNotificationSettings = false,
}: Props) => {
  const {
    adBudgetOrderId,
    draftId,
    flowType,
    jobRevisionId,
    jobOrderId,
    cartOrderId,
    pageId,
    productSetId,
    seekAdvertiserId,
    currentLoginUserEmail,
    currentLoginUserId,
    initialNotificationPreference,
    initialTeamMemberJobAccessList,
  } = useSelector(
    (state: AppState) => ({
      adBudgetOrderId: state.session.adBudget?.orderId,
      draftId: state.session.draftId,
      flowType: state.tealium.cajaFlowType,
      jobRevisionId: state.session.job?.revisionId,
      jobOrderId: state.session.jobOrderId,
      cartOrderId: state.session.cart.orderId,
      pageId: state.session.pageId,
      productSetId: state.session.productSet?.id,
      seekAdvertiserId: state.session.seekAdvertiserId,
      currentLoginUserEmail: state.session.userEmailAddress,
      currentLoginUserId: state.session.userId,
      initialNotificationPreference:
        state.session.notificationsSetting.savedNotificationPreference,
      initialTeamMemberJobAccessList:
        state.session.notificationsSetting.savedTeamMemberJobAccessList,
    }),
    shallowEqual,
  );

  const [hubbleEventPosted, setHubbleEventPosted] = useState(false);
  const hubble = useHubble();
  const oldFlowOrderId = adBudgetOrderId ?? jobOrderId;
  const orderId = cartOrderId ?? oldFlowOrderId;
  const dispatch = useDispatch();

  useEffect(() => {
    if (flowType === 'create' && !hubbleEventPosted) {
      if (
        draftId &&
        pageId &&
        productSetId &&
        orderId &&
        jobId &&
        jobRevisionId
      ) {
        hubble.trackEvent('job_post_submit_pressed', {
          draftId,
          postingFlowType: flowType,
          postingFlowPageName: pageId,
          productSetId,
          orderId,
          jobId,
          jobRevisionId,
          opportunityId: draftId,
        });
        setHubbleEventPosted(true);
        // Resetting draftId for session only to avoid usage of completed draft
        dispatch(resetSessionDraftId());
      } else {
        log.error(
          `Success | SuccessBody | Unable to fire job_post_submit_pressed hubble event for create flow`,
          {
            seekAdvertiserId,
            draftId,
            postingFlowType: flowType,
            postingFlowPageName: pageId,
            productSetId,
            orderId,
            jobId,
            jobRevisionId,
          },
        );
      }
    }
  }, [
    draftId,
    flowType,
    pageId,
    productSetId,
    orderId,
    jobId,
    jobRevisionId,
    hubble,
    seekAdvertiserId,
    hubbleEventPosted,
    dispatch,
  ]);

  if (!jobId) {
    return <SuccessBodyContent jobId={jobId} />;
  }

  return (
    <ApolloConsumer>
      {(client) => (
        <HirerNotificationPreferenceProvider
          jobId={jobId}
          seekAdvertiserId={seekAdvertiserId}
          currentLoginUserId={currentLoginUserId}
          currentLoginUserEmail={currentLoginUserEmail}
          initialNotificationPreference={initialNotificationPreference}
          apolloClient={client}
          delayUpdate
        >
          <HirerTeamMemberJobAccessProvider
            jobId={jobId}
            seekAdvertiserId={seekAdvertiserId}
            currentLoginUserId={currentLoginUserId}
            initialTeamMemberJobAccessList={initialTeamMemberJobAccessList}
            apolloClient={client}
            delayUpdate
          >
            {inNotificationSettings ? (
              <NotificationsSetting />
            ) : (
              <SuccessBodyContent jobId={jobId} />
            )}
          </HirerTeamMemberJobAccessProvider>
        </HirerNotificationPreferenceProvider>
      )}
    </ApolloConsumer>
  );
};
